// Prequalification funnel — authored copy + option catalogs.
//
// Fit-check questions (prequal-v2 polarity): phrased so "Yes" is the
// FAVORABLE answer — "No" soft-declines, "Unsure" flags, unanswered is
// inert. The keys mirror PrequalKnockoutKeys.cs (the engine's contract) and
// stay UNCHANGED from the v1 disclosure era; only the display text and
// polarity changed. Copy rules: Phoenix Capital is the Loan/Note
// Administrator (never "broker" / "securities firm"); every figure is
// labeled indicative; program terms are stated as the program documents state
// them (the note holder decides; Phoenix sets no terms).

// Type-only, so it is erased at build and introduces no runtime import cycle
// (PrequalBrandContext imports both this module and that one).
import type {
  PrequalFunnelCopy,
  PrequalFunnelMarketingCopy,
} from "../../api/prequalFunnelCopy";

export interface ChoiceOption {
  value: string;
  label: string;
}

/** Declarative spec for a restructured knockout that presents a PRIMARY
 *  control plus a conditional FOLLOW-UP control, resolving the pair to the
 *  single engine-facing value the fixed-key engine loop already reads. Driven
 *  by <c>RestructuredKnockout</c> inside <c>KnockoutChecklist</c> so the
 *  aggregate "needs a conversation" note keeps working unchanged. */
export interface KnockoutFollowUpDef {
  /** PRIMARY control options (overrides the default Yes / No / Unsure). */
  primaryOptions: ChoiceOption[];
  /** The primary value that reveals the follow-up control. */
  trigger: string;
  /** Optional primary option to visually emphasize (subtle gold outline when
   *  NOT selected) — the "expected" answer for a polarity-flipped question. */
  emphasizePrimary?: string;
  /** Follow-up question text. */
  followUpQuestion: string;
  /** Follow-up control options. */
  followUpOptions: ChoiceOption[];
  /** A follow-up value that RESETS the primary back to <c>resetPrimaryTo</c>
   *  and hides the follow-up (the "No, let me change my answer" branch). */
  resetOnFollowUp?: string;
  resetPrimaryTo?: string;
  /** Maps (primary, followUp | null) → the engine-facing stored value, or
   *  null when the pair is not yet resolvable (follow-up still pending). */
  resolve: (primary: string, followUp: string | null) => string | null;
}

export interface KnockoutQuestionDef {
  key: string;
  statement: string;
  /** Optional secondary/helper line rendered under the statement (§A). */
  helperText?: string;
  /** Per-question answer options override (the franchise question renders
   *  Yes / No / "Not a franchise"); default = Yes / No / Unsure. */
  answerOptions?: ChoiceOption[];
  /** When present, the row renders as a primary + conditional-follow-up
   *  control (via RestructuredKnockout) instead of a single control. */
  followUp?: KnockoutFollowUpDef;
  /** Warm, plain-language explanation of what a restricting answer means plus
   *  a next step, shown once this question's RESOLVED engine value is "no".
   *  The hard-stop-vs-softer-flag distinction is carried by TONE in this copy
   *  only — the engine outcome (SoftDecline) is identical for every "no". */
  restrictExplanation?: string;
  /** Light educational note shown once the RESOLVED value is "unsure" — never
   *  a restriction; deliberately distinct from restrictExplanation. */
  unsureNote?: string;
}

/** Stored answer value for the franchise question's third option — behaves
 *  as a PASS (not a flag) in both engines. */
export const NOT_A_FRANCHISE_VALUE = "not-a-franchise";

// ── Fit-check restructure helpers (§B/§C/§D/§E) ─────────────────────────
// Shared option sets + follow-up specs. Every restructure resolves to an
// EXISTING engine value ("yes" | "no" | "unsure" | "not-a-franchise"); the
// server engine and the TS mirror (evaluateKnockouts) read only the bare key.

const YES_NO_UNSURE: ChoiceOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unsure", label: "Unsure" },
];

const YES_NO_PRIMARY: ChoiceOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const CONFIRM_OPTIONS: ChoiceOption[] = [
  { value: "yes", label: "Yes, that's right" },
  { value: "no", label: "No, let me change my answer" },
];

/** §B/§D — polarity-flipped excluded-industry question: the PRIMARY asks
 *  whether the business operates IN an excluded industry (so "No" is the
 *  clean/expected answer, emphasized), and the rare "Yes" branch gets a
 *  confirming follow-up. Resolves to the engine's existing value — display
 *  No → "yes" (clear), Unsure → "unsure", Yes + "Yes, that's right" → "no"
 *  (excluded). "No, let me change my answer" resets the primary to No. */
function excludedIndustryFollowUp(followUpQuestion: string): KnockoutFollowUpDef {
  return {
    primaryOptions: YES_NO_UNSURE,
    trigger: "yes",
    emphasizePrimary: "no",
    followUpQuestion,
    followUpOptions: CONFIRM_OPTIONS,
    resetOnFollowUp: "no",
    resetPrimaryTo: "no",
    resolve: (primary, followUp) => {
      if (primary === "no") return "yes";
      if (primary === "unsure") return "unsure";
      if (primary === "yes") return followUp === "yes" ? "no" : null;
      return null;
    },
  };
}

/** §confirm — wraps a plain Yes/No/Unsure knockout with a CONFIRM step: the
 *  restricting "No" reveals a confirm follow-up. "Yes, that's right" locks in
 *  the restriction (engine "no"); "No, let me change my answer" resets the
 *  primary to unanswered (so the visitor re-picks). Yes → "yes" (favorable),
 *  Unsure → "unsure" (flag). Renders through the SAME RestructuredKnockout
 *  path as the excluded-industry questions — no polarity flip, no emphasis. */
function confirmFollowUp(followUpQuestion: string): KnockoutFollowUpDef {
  return {
    primaryOptions: YES_NO_UNSURE,
    trigger: "no",
    followUpQuestion,
    followUpOptions: CONFIRM_OPTIONS,
    resetOnFollowUp: "no",
    resetPrimaryTo: "",
    resolve: (primary, followUp) => {
      if (primary === "yes") return "yes";
      if (primary === "unsure") return "unsure";
      if (primary === "no") return followUp === "yes" ? "no" : null;
      return null;
    },
  };
}

// ── Buyer fit-check questions (7) ───────────────────────────────────────
// (Day-to-day involvement is captured by the dedicated active-management
// question below, not as a knockout row.)

export const BUYER_KNOCKOUTS: KnockoutQuestionDef[] = [
  {
    key: "buyer-bankruptcy",
    statement:
      "Are your personal finances clear of any active bankruptcy (Chapter 7 or 13)?",
    followUp: confirmFollowUp(
      "To confirm: you currently have an active bankruptcy (Chapter 7 or 13) that hasn't been discharged — is that right?",
    ),
    restrictExplanation:
      "While a bankruptcy is still open, we're not able to move forward with financing through Phoenix — that's a requirement across the program, not a judgment about you. Once it's discharged, we'd genuinely welcome the conversation. If the timing is close, reach out and we'll help you plan around it.",
    unsureNote:
      "“Active” means a case that's been filed and isn't yet discharged. If you're not sure where yours stands, it's fine to check and come back.",
  },
  {
    key: "buyer-ofac",
    statement:
      "Are all owners and principals clear of federal sanctions lists (OFAC)?",
    followUp: confirmFollowUp(
      "To confirm: one or more owners or principals may appear on a federal sanctions list (OFAC) — is that right?",
    ),
    restrictExplanation:
      "Federal law prevents any lender, including Phoenix, from financing a party on the OFAC sanctions list, so we're not able to move forward while that's the case. These lists sometimes flag a name by mistake — if you think that's what happened, please reach out and we'll help you sort it out.",
    unsureNote:
      "Most people are clear here — OFAC is the U.S. Treasury's sanctions list. If you're not certain, mark it and we can check together.",
  },
  {
    key: "buyer-felony",
    statement:
      "Are all owners free of felony convictions in the past 10 years involving financial fraud, embezzlement, theft, or money laundering?",
    followUp: confirmFollowUp(
      "To confirm: an owner has a felony conviction in the past 10 years involving financial fraud, embezzlement, theft, or money laundering — is that right?",
    ),
    restrictExplanation:
      "A financial-crime conviction within the past 10 years is a firm requirement across Phoenix's program — it applies the same way for every applicant, not as a judgment on your specific situation. If you believe this doesn't accurately describe your record, please reach out and we can look into it.",
    unsureNote:
      "This only covers financial felonies — fraud, embezzlement, theft, or money laundering — in the last 10 years. If you're unsure, it's fine to flag it.",
  },
  {
    key: "buyer-other-debt",
    statement:
      "Will this loan be your only acquisition financing — no bank, SBA, or other loans alongside it?",
    followUp: confirmFollowUp(
      "To confirm: you plan to use other financing — a bank loan, SBA loan, or similar — alongside this loan to buy the business — is that right?",
    ),
    restrictExplanation:
      "Phoenix is designed to be the single financing behind the purchase, so another acquisition loan alongside ours changes the structure. That doesn't always end the conversation — sometimes it can be reworked — so let's talk it through before you rule anything out.",
    unsureNote:
      "This is about acquisition financing used to buy the business, not the business's everyday operating debt. If you're weighing options, mark it and we'll help you think it through.",
  },
  {
    key: "buyer-no-guarantee",
    statement:
      "Is every owner of 20% or more of the acquiring entity willing and able to sign a personal guarantee?",
    followUp: confirmFollowUp(
      "To confirm: an owner of 20% or more of the acquiring entity is not willing or able to personally guarantee this loan — is that right?",
    ),
    restrictExplanation:
      "A personal guarantee means each owner who signs is personally responsible for the loan if the business can't repay it — that's a standard requirement across Phoenix's financing structure, not something specific to your deal. Without it from every owner of 20% or more, we're not able to move forward with financing through Phoenix at this time. If your ownership structure changes, or you'd like to talk it through, we're happy to help.",
    unsureNote:
      "That's worth checking on — a personal guarantee means each owner of 20% or more agrees to be personally responsible for repaying the loan. It's fine to come back to this once you've talked it over.",
  },
  {
    key: "buyer-excluded-industry",
    statement:
      "Does the business you're targeting operate in, or have ties to, any of the following industries: cannabis or cannabis-adjacent, adult entertainment, gambling, or cryptocurrency/digital assets?",
    followUp: excludedIndustryFollowUp(
      "To confirm: the business you're targeting does have a connection to one of these industries (cannabis or cannabis-adjacent, adult entertainment, gambling, or cryptocurrency/digital assets) — is that right?",
    ),
    // Confirm step already provided by excludedIndustryFollowUp — explain only.
    restrictExplanation:
      "Phoenix isn't able to finance businesses in these industries — cannabis, adult entertainment, gambling, or crypto/digital assets — because of how our financing and banking partners are structured. It's a firm line for us, so we're not able to move forward with a target in one of these areas. If the business you had in mind falls outside them, feel free to adjust your answer above.",
  },
  {
    key: "buyer-phoenix-default",
    statement:
      "Do you or any acquiring owner currently have loans or notes outstanding with Phoenix Capital?",
    followUp: {
      primaryOptions: YES_NO_PRIMARY,
      trigger: "yes",
      followUpQuestion: "Are all of those obligations current and in good standing?",
      followUpOptions: YES_NO_UNSURE,
      // No obligations → clear ("yes"). The good-standing follow-up resolves
      // yes → "yes" (clear), no → "no" (knockout), unsure → "unsure". The
      // primary companion key (`buyer-phoenix-default__primary`) persists the
      // has-obligations branch, so a restored session distinguishes "no
      // obligations" from "has obligations, all in good standing" — both of
      // which resolve to the same engine value "yes".
      resolve: (primary, followUp) => {
        if (primary === "no") return "yes";
        if (primary === "yes") {
          if (followUp === "yes") return "yes";
          if (followUp === "no") return "no";
          if (followUp === "unsure") return "unsure";
        }
        return null;
      },
    },
    // The follow-up IS the disambiguating question — no extra confirm. The
    // explanation shows on the restricting resolved value ("no"); the unsure
    // note on "unsure".
    restrictExplanation:
      "Because you already have financing with Phoenix that isn't current, we'll want to look at that together before adding anything new — it doesn't shut the door, it just means a conversation first. If the standing has changed or there's context, reach out and we'll take a fresh look.",
    unsureNote:
      "If you're not sure whether every Phoenix obligation is current, we can confirm it with you — it won't hold up your result.",
  },
];

// ── Seller fit-check questions (9) ──────────────────────────────────────

export const SELLER_KNOCKOUTS: KnockoutQuestionDef[] = [
  {
    key: "seller-no-authority",
    statement:
      "Do you hold clear, uncontested legal authority to sell this business?",
    followUp: confirmFollowUp(
      "To confirm: your authority to sell this business isn't fully clear, or someone else is contesting it — is that right?",
    ),
    restrictExplanation:
      "Whoever signs needs clean authority to sell, so if that's in question it's worth sorting out first — often it's just getting a co-owner's sign-off. This isn't a 'no'; let's talk about what it would take to get you there.",
    unsureNote:
      "If you're not certain — say there are other owners or a partnership involved — mark it and we'll help you figure out where things stand.",
  },
  {
    key: "seller-surviving-debt",
    statement:
      "If you move forward with Phoenix, will Phoenix's note be the only financing used to fund this transaction?",
    helperText:
      "This is about financing used to help close this sale — for example, a hard money loan, personal loan, or SBA loan brought in by either party to fund the purchase. It does not include the business's everyday operating debt (credit cards, equipment leases, working capital lines of credit, etc.).",
    followUp: confirmFollowUp(
      "To confirm: financing other than Phoenix's note — like a hard money, personal, or SBA loan — would be used to help fund this sale — is that right?",
    ),
    restrictExplanation:
      "Phoenix is built to be the single financing behind the sale, so another loan used to fund the purchase changes the structure. It's not always a dealbreaker — let's talk through your situation before you set it aside. (This is only about financing used to close the sale, not the business's everyday operating debt.)",
    unsureNote:
      "This is about money brought in to fund the purchase itself, not normal operating debt. If you're not sure, flag it and we'll walk through it.",
  },
  {
    key: "seller-bankruptcy",
    statement: "Is the business free of any active bankruptcy proceeding?",
    followUp: confirmFollowUp(
      "To confirm: the business is currently in an active bankruptcy proceeding — is that right?",
    ),
    restrictExplanation:
      "While a business is in an active bankruptcy, we're not able to finance its sale through Phoenix — that's a program-wide requirement. Once the proceeding is resolved, we'd be glad to revisit it with you.",
    unsureNote:
      "“Active” means a bankruptcy that's been filed and isn't yet resolved. If you're not sure of the status, it's fine to check and come back.",
  },
  {
    key: "seller-excluded-industry",
    statement:
      "Does the business operate in, or have ties to, any of the following industries: cannabis or cannabis-adjacent, adult entertainment, gambling, or cryptocurrency/digital assets?",
    followUp: excludedIndustryFollowUp(
      "To confirm: the business does have a connection to one of these industries (cannabis or cannabis-adjacent, adult entertainment, gambling, or cryptocurrency/digital assets) — is that right?",
    ),
    // Confirm step already provided by excludedIndustryFollowUp — explain only.
    restrictExplanation:
      "Phoenix isn't able to finance businesses in these industries — cannabis, adult entertainment, gambling, or crypto/digital assets — because of how our financing and banking partners are structured. It's a firm line for us, so we're not able to move forward with this business. If that's not an accurate description of it, feel free to adjust your answer above.",
  },
  {
    key: "seller-nontransferable-license",
    statement:
      "Can all required licenses and certifications transfer to a buyer — none tied to you personally?",
    followUp: confirmFollowUp(
      "To confirm: one or more required licenses or certifications are tied to you personally and may not transfer to a buyer — is that right?",
    ),
    restrictExplanation:
      "If a license the business needs can't move to the new owner, that affects whether a buyer can keep operating, so it's something we'll want to look at closely. It doesn't necessarily stop the deal — sometimes there's a path. Let's talk it through.",
    unsureNote:
      "If you're not sure whether a license would transfer, mark it — we can look into it together, and it won't block your result.",
  },
  {
    key: "seller-tax-lien",
    statement:
      "Is the business clear of IRS or state tax liens that could not be resolved at or before closing?",
    followUp: confirmFollowUp(
      "To confirm: the business has an IRS or state tax lien that couldn't be cleared at or before closing — is that right?",
    ),
    restrictExplanation:
      "Most tax liens get paid off from the sale proceeds at closing, so they're rarely a dealbreaker. If there's one that genuinely can't be resolved by then, we'll want to understand it — reach out and we'll figure out the options with you.",
    unsureNote:
      "Many liens simply get paid at closing. If you're unsure whether one could be cleared in time, flag it and we'll help you sort it out.",
  },
  {
    key: "seller-investigation",
    statement:
      "Is the business free of active regulatory investigations, enforcement actions, or stop-work orders?",
    followUp: confirmFollowUp(
      "To confirm: the business currently has an active regulatory investigation, enforcement action, or stop-work order — is that right?",
    ),
    restrictExplanation:
      "An open regulatory matter is something we need to understand before financing a sale, since it can affect the business's operations and value. Depending on what it is, there may still be a path — let's talk about it rather than assume the worst.",
    unsureNote:
      "If you're aware of something but not sure it counts, mark it — better to talk it through than guess. It won't hold up your result.",
  },
  {
    key: "seller-not-going-concern",
    statement:
      "Is the business operating normally today — an active going concern?",
    followUp: confirmFollowUp(
      "To confirm: the business isn't operating normally right now — it's paused, winding down, or not currently a going concern — is that right?",
    ),
    restrictExplanation:
      "Phoenix sizes financing off verified, current cash flow, so while the business isn't actively operating, there isn't yet a basis to size a loan against. Once it's back up and running with a track record, we'd be glad to take another look.",
    unsureNote:
      "“Going concern” just means the business is actively operating and expected to keep running. If your situation is in between, flag it and we'll talk.",
  },
  {
    key: "seller-franchisor-denied",
    statement: "Is the business operated under a franchise agreement?",
    followUp: {
      primaryOptions: YES_NO_PRIMARY,
      trigger: "yes",
      followUpQuestion:
        "Has the franchisor consented to the transfer, or is consent not yet denied in writing?",
      followUpOptions: YES_NO_UNSURE,
      // No franchise → the existing "not-a-franchise" pass value; a franchise
      // resolves the consent follow-up straight to yes/no/unsure.
      resolve: (primary, followUp) => {
        if (primary === "no") return NOT_A_FRANCHISE_VALUE;
        if (primary === "yes") {
          if (followUp === "yes") return "yes";
          if (followUp === "no") return "no";
          if (followUp === "unsure") return "unsure";
        }
        return null;
      },
    },
    // The consent follow-up IS the disambiguating question — no extra confirm.
    restrictExplanation:
      "If the franchisor won't consent to the transfer, a buyer can't step into the franchise, so we'd need to understand where that stands before going further. Franchisor decisions sometimes change with the right buyer — let's talk about it. (If the business isn't a franchise, choose “No” on the first question above.)",
    unsureNote:
      "If you're not sure where the franchisor stands on a transfer, that's okay — we can work through it with you, and it won't block your result.",
  },
];

// ── Buyer option catalogs ───────────────────────────────────────────────

export const PRICE_BANDS: ChoiceOption[] = [
  { value: "exploring", label: "Still exploring" },
  // Joined when the program minimum fell to $250,000 (2026-10-16). A NEW
  // stored value; every older one keeps its exact meaning.
  { value: "250k-500k", label: "$250K–$500K" },
  { value: "500k-1m", label: "$500K–$1M" },
  { value: "1m-2.5m", label: "$1M–$2.5M" },
  { value: "2.5m-5m", label: "$2.5M–$5M" },
  { value: "5m-10m", label: "$5M–$10M" },
];

/**
 * Which package the buyer asked for. The two values are the SERVER's enum
 * member names — `PrequalBuyerDocumentPath` is string-stored, so what the funnel
 * sends is what lands in the column, and a renamed member here would silently
 * stop matching on the other side.
 */
export const DOCUMENT_PATH_LETTER_ONLY = "LetterOnly";
export const DOCUMENT_PATH_WITH_DEAL_SHEET = "LetterAndDealSheet";
export const DOCUMENT_PATH_INTERNAL = "InternalBuyer";

/**
 * One arm of the opening fork, as a CARD rather than a chip.
 *
 * Deliberately not a {@link ChoiceOption}: that carries a value and a label and
 * nothing else, which is the right shape for a pill in a row of pills and the
 * wrong one here. This question decides which documents Phoenix generates and,
 * on the third arm, what the qualification is sized from — so each arm needs
 * room to say what it is and what it produces, and a three-line card is the
 * smallest thing that can.
 */
export interface DocumentPathCard {
  /** The wire value — a `PrequalBuyerDocumentPath` member name. */
  value: string;
  /** The buyer’s own sentence: who they are, in their words. */
  label: string;
  /** What they want done. */
  body: string;
  /** What Phoenix hands back if they pick this — the line that makes the
   *  choice answerable without knowing the vocabulary. */
  produces: string;
}

/**
 * The fork, on its own first step.
 *
 * REQUIRED and deliberately un-defaulted: it decides which documents Phoenix
 * generates AND, on the third arm, what the qualification is sized from —
 * pre-selecting any of them is Phoenix answering on the buyer's behalf. A buyer
 * who wanted only proof of buying power should not have to notice that a Deal
 * Sheet was chosen for them; a buyer looking at a real listing should not have
 * to discover afterwards that nobody priced it; and a buyer buying the company
 * they already work at should not be measured against a down payment the
 * program does not ask them for.
 *
 * The LABEL is the buyer’s own sentence about themselves and the BODY is what
 * they want done, because a buyer arriving here has no idea what a "Deal Sheet"
 * is yet. `produces` is the third line and the one that earns the card: it names
 * what Phoenix HANDS BACK for each arm, so the choice is made against its
 * outcome rather than against a phrase the buyer is still decoding. The fork
 * used to sit above seven other fields and was silently required — a buyer
 * filled the rest of the step, pressed Continue, and only then met "Choose what
 * you need from Phoenix to continue" rendered at the foot of the page, two
 * screens from the question. It has its own step now, which makes that
 * unreachable rather than merely unlikely.
 *
 * 🔴 **THE THIRD ARM IS NOT A THIRD PACKAGE.** The first two choose DOCUMENTS
 * and size nothing — qualification is still the buyer's capital through the
 * factor ladders either way. `InternalBuyer` routes the engine onto a different
 * BASIS entirely: the business's own cash flow, with no down-payment ratio, no
 * ladders and no first-timer cap. It sits here rather than on a later step
 * because everything after it — what is asked, what gates, what is produced —
 * depends on the answer, and asking later would mean asking a buyer with no
 * capital of their own to justify capital first.
 */
export const DOCUMENT_PATH_CARDS: DocumentPathCard[] = [
  {
    value: DOCUMENT_PATH_LETTER_ONLY,
    label: "I’m still looking",
    body: "Show me what I can afford, and give me a letter I can shop with.",
    produces: "You get a prequalification letter and the program brochure.",
  },
  {
    value: DOCUMENT_PATH_WITH_DEAL_SHEET,
    label: "I have a business in mind",
    body: "Price that deal and tell me whether it works.",
    produces: "You get a Deal Sheet on that business, plus the letter and the brochure.",
  },
  {
    value: DOCUMENT_PATH_INTERNAL,
    label: "I’m buying in where I already am",
    body: "I’m family, an employee, or a partner in the business I’m buying.",
    produces: "You get an internal-purchase Deal Sheet and letter, plus the brochure.",
  },
];

/**
 * How an internal buyer is connected to the business they are buying. The
 * VALUES are the C# `PrequalInternalRelationship` member names — string-stored,
 * so these are the wire values rather than a client-side label set, and the
 * union guard pins them to the enum member for member.
 *
 * Three qualify and no others. A supplier, a customer or an adviser is not an
 * internal buyer however well they know the business: the premise of the path
 * is that the buyer is already INSIDE the company whose cash flow is about to
 * qualify the purchase, which is what makes sizing on that cash flow — rather
 * than on their own bank balance — the honest measure.
 */
export const INTERNAL_RELATIONSHIP_OPTIONS: ChoiceOption[] = [
  {
    value: "FamilyMember",
    label: "I'm family — I'm buying from a relative",
  },
  {
    value: "EmployeeOrManager",
    label: "I work there — employee or manager",
  },
  {
    value: "PartnerOrCoOwner",
    label: "I'm already a partner or co-owner",
  },
];

/** The one relationship that is asked how long they have been there. Employment
 *  is the only one of the three with a tenure to state — family is a fact about
 *  a person and a partnership has its own paperwork. There is NO MINIMUM and it
 *  never gates: a manager of eight months is as eligible as one of eight years,
 *  and the answer is context for the specialist, not a threshold. */
export const INTERNAL_RELATIONSHIP_WITH_TENURE = "EmployeeOrManager";

/**
 * The band a STATED purchase price falls in, or `null` when none does.
 *
 * 🔴 A buyer who states a price is not asked for a band — they have given a
 * specific figure, and asking for a range afterwards is asking the same question
 * twice and worse. But the band is NOT redundant downstream: it is read by the
 * staff answer panel, by `PrequalAnalysisCalculator.StatedTargetLabel` on the
 * Analysis document, and twice in the AI grounding payload. So the QUESTION is
 * skipped and the ANSWER is derived — dropping the field would quietly cost
 * those three an input the buyer has in fact answered more precisely than the
 * band ever could.
 *
 * ⚠️ This used to say the band "is stored as `BuyerLead.StatedPriceBand`" and
 * "is what the listing-research engine filters on". Both are FALSE of this
 * funnel: `ApplyPrequalOwnedFields` writes `MaxPurchasePrice`,
 * `DownPaymentAvailable`, `TargetIndustries` and `SourceDetail` and nothing
 * price-shaped, so a prequal-originated buyer reaches listing research with NO
 * stated band. That column's only writers are the `/buy` landing intake and a
 * direct `CreateBuyerLeadRequest`. The gap is real and pre-existing — the two
 * were designed to meet and do not — but it is not what this derivation is for,
 * and a reader planning around the old sentence would be planning around a
 * column nothing here fills.
 *
 * Out of range → `null`, and deliberately not a band. There is no band below
 * a $250K purchase price and none above $10M (the bands are price buckets; the
 * program minimum itself is $250,000 of amount financed, `MinAmountFinanced`),
 * and `exploring` is not available as a catch-all: it means "still
 * exploring", which is false of a buyer who has just named a business and its
 * price. A missing band is a missing answer a specialist can ask about; a wrong
 * one is a filter quietly excluding the right listings.
 *
 * Kept HERE, beside {@link PRICE_BANDS}, so the list and the mapping cannot
 * drift — the five upper bounds below are that list's own boundaries. The
 * lowest edge, 250,000, is a PRICE bucket edge with its own literal,
 * decoupled from the financed minimum so a change to that minimum never
 * re-buckets stored bands; `programMinimumBandEdge.test.ts` pins the literal.
 * The 500,000 edge is likewise a bucket edge.
 */
export function priceBandForAmount(amount: number | null): string | null {
  if (amount == null || amount <= 0) return null;
  if (amount < 250_000) return null;
  if (amount < 500_000) return "250k-500k";
  if (amount <= 1_000_000) return "500k-1m";
  if (amount <= 2_500_000) return "1m-2.5m";
  if (amount <= 5_000_000) return "2.5m-5m";
  if (amount <= 10_000_000) return "5m-10m";
  return null;
}

/**
 * The reserves pair, in the order the buyer is asked: the dollar amount, then
 * the band that translates it into months of expenses.
 *
 * 🔴 Only the BAND sizes anything. It is factor ladder `F_res`'s input in
 * `prequalSizing.ts` and in its C# source of truth; the amount beside it is
 * captured, stored and shown, and reaches no figure on any document. That is a
 * ruling, not an omission — re-keying the ladder to dollars would change what
 * every capital-path buyer qualifies for, which is a credit decision with its
 * own evidence and its own approval.
 *
 * Kept here rather than inline at the field, with the band's own options, for
 * the reason the stale industry helper was wrong: a bare inline literal is copy
 * nobody finds when the thing it describes changes.
 */
/**
 * The "which business?" question, which asks three different things depending
 * on the arm the buyer picked at the top of the funnel.
 *
 * - **Internal**: REQUIRED. The Deal Sheet is titled with the company this
 *   buyer is buying into, and staff verify the relationship to it; neither the
 *   heading nor the check can be written about a business nobody named.
 * - **Deal**: REQUIRED, and asked FIRST on that arm. The buyer has just chosen
 *   the arm that prices one named business, so "is there a specific business?"
 *   asks something they answered by choosing it. It asks for the NAME instead,
 *   above the figures, because the figures are figures ABOUT something and a
 *   Deal Sheet whose subject nobody named is a price attached to no business.
 * - **Letter-only**: still a genuine question, and still OPTIONAL — this buyer
 *   may be shopping a category and nothing more.
 *
 * 🔴 THE NAME NOW TITLES THE BUYER DEAL SHEET, AND THAT IS A CHANGE THIS COPY
 * IS COUPLED TO RATHER THAN A FACT IT CAN CHECK. Until the buyer sheet grew a
 * business-name member, `PrequalificationSubmission.TargetBusinessName` reached
 * ZERO document renderers — the sheet's only business-name slot sat on
 * `SellerDealSheetModel`, fed from `seller?.BusinessName` and null on every
 * buyer sheet — so the deal helper truthfully ended "prints on nothing we send
 * out". It no longer does. If the renderer change is ever reverted, THIS COPY
 * GOES FALSE WITH IT and is the thing to fix, not the sentence to keep.
 *
 * 🔴 AND IT STILL DOES NOT REACH THE LETTER OR THE COMPLETION EMAIL. That is a
 * ruling, not an omission: the letter's reader is the seller, and naming a
 * business a buyer typed wrong is worse than naming none. `BuyerLetterModel`
 * carries no business-name member at all, and `{{BusinessName}}` is registered
 * on SELLER-letter blocks only. A helper on any arm that puts this name on the
 * letter is claiming something no renderer can do — which the internal helper
 * did from the day it shipped until it was corrected here.
 *
 * ⚠️ AND THE COUPLING IS INVISIBLE TO A GREP OF THE RENDERER. The buyer model
 * takes the value as a member called `BusinessName` — mirroring the seller's —
 * while the SERVICE supplies `buyer?.TargetBusinessName` from outside that file,
 * so searching the deal-sheet renderer for the column's own name finds nothing
 * either before or after the change. What this copy leans on is the BEHAVIOUR —
 * the rendered sheet is headed with the business — and that is what has to stay
 * pinned on the C# side for this sentence to keep being true.
 *
 * Beyond the heading it reaches STAFF (search, the detail page, and
 * `ResolveIndustryOrBusiness`, which prefers it over the industry) — which is
 * the other half of what the deal helper promises, and between them all it
 * promises. It changes no figure: the price comes from the asking-price field.
 */
export const TARGET_BUSINESS_LABEL_INTERNAL = "What is the business called?";
export const TARGET_BUSINESS_LABEL_DEAL = "What is the business called?";
export const TARGET_BUSINESS_LABEL_LETTER_ONLY =
  "Is there a specific business you’re looking at? (optional)";

export const TARGET_BUSINESS_HELPER_INTERNAL =
  "The company you’re buying into. It titles your Deal Sheet.";
export const TARGET_BUSINESS_HELPER_DEAL =
  "A name, a listing title, or a link — whatever you have. It titles your Deal "
  + "Sheet and goes to the consultant who picks this up, so they know which deal "
  + "you mean. It changes no figure.";
export const TARGET_BUSINESS_HELPER_LETTER_ONLY =
  "A name, a listing title, or a link — whatever you have.";

/**
 * The sizing meter before it can size anything.
 *
 * The rail used to render NOTHING until a figure existed, which reads as a card
 * that has not loaded rather than a question that has not been asked yet. It
 * says so now.
 *
 * 🔴 NO STEP ORDINAL, EVER. This sentence used to end "It appears here from
 * step 2 onward", and the number was wrong twice over: it SHIPPED — the old
 * docstring claimed the off-by-one was "reported in the PR rather than shipped",
 * which was false, and a buyer could check it against the stepper two inches
 * away — and it is a fact about the step ORDER, which the funnel reshuffles. The
 * restructure that split the package fork onto its own first step moved it
 * again. A sentence naming a position is guaranteed to go stale on the next
 * reshuffle and says nothing the buyer needs; what they need is the CONDITION,
 * which does not move.
 *
 * It is also rendered on the SELLER funnel — `SizingMeter` is shared — where an
 * ordinal counted against the buyer's steps was never right at all.
 */
export const SIZING_METER_PENDING_NOTE =
  "Not yet — we work this out from what you can put in and your background. "
  + "It appears here once you’ve told us what you can put in.";

/**
 * What the buyer's band says when the program floor is the only thing holding
 * it up (M30 / M33).
 *
 * Since prequal-v7 the capital clamp floors the FINANCED part of all three
 * figures at `minAmountFinanced` and adds the buyer's own capacity back (D3), so
 * a buyer whose capital sizes below it gets a band whose ends are equal — the
 * minimum financed plus what they put down. Rendering that as "$258,000 –
 * $258,000" presents a floor as a range and invites the reader to believe their
 * own answers produced both numbers. One figure, and the reason for it. The
 * caller formats the minimum from `PREQUAL_PROGRAM_TERMS.minAmountFinanced` (this
 * module cannot import it).
 *
 * ⚠️ No existing fixture reaches this state: `PrequalBits.test.tsx`'s `BEFORE`
 * is hand-written with `buyingPowerLow: 442_000` beside a `qualifiedAmount:
 * 500_000` that the old $500,000 floor clamped, which the capital arm cannot
 * produce — it clamps the low end through the same `programClamp`. Untested by construction, which
 * is why it survived.
 */
export function programMinimumBandNote(minimumFinanced: string): string {
  return `Your figure sits at the program minimum — ${minimumFinanced} financed, plus what `
    + "you put down — rather than a range your answers produced.";
}

export const RESERVES_AMOUNT_LABEL =
  "How much working capital will you have after closing? (optional)";

export const RESERVES_AMOUNT_HELPER =
  "What is left in the business and in your own accounts once the deal is done. "
  + "Zero is a real answer. It gives us context for the conversation and changes "
  + "no figure here.";

export const RESERVES_BAND_QUESTION = "Roughly how many months of expenses is that?";

export const RESERVES_BANDS: ChoiceOption[] = [
  { value: "under-3mo", label: "Under 3 months of expenses" },
  { value: "3-6mo", label: "3–6 months" },
  { value: "6mo-plus", label: "6+ months" },
];

// §3 — the four prequal-v3 buyer ladder-chip questions. Values bind to the
// engine's PREQUAL_BUYER_FACTOR_LADDERS vocabularies (prequalSizing.ts;
// PrequalProgramTerms.cs is the source of truth). Ordered ascending by rung.

export const OWNERSHIP_LADDER: ChoiceOption[] = [
  { value: "never-owned", label: "Never owned a business" },
  { value: "owned-smaller", label: "Owned a smaller/different business (<3 yrs)" },
  { value: "owned-comparable", label: "Owned a comparable business (3+ yrs)" },
  { value: "owned-multiple", label: "Owned multiple / built & sold" },
];

export const MANAGEMENT_LADDER: ChoiceOption[] = [
  { value: "none", label: "None" },
  { value: "small-team", label: "Led a small team (2–10)" },
  { value: "department-pnl", label: "Ran a department w/ P&L (10–50)" },
  { value: "executive-pnl", label: "Full operations / exec P&L (50+)" },
];

export const INDUSTRY_LADDER: ChoiceOption[] = [
  { value: "new", label: "New to this industry" },
  { value: "adjacent", label: "Adjacent / related" },
  { value: "direct", label: "Direct, 1–5 years" },
  { value: "direct-5plus", label: "Direct 5+ yrs / licensed in trade" },
];

// ── Active-management (involvement) + its full-time-management follow-up ──
// Lives on the background step. "operator" is the favorable PASS with no
// follow-up. A non-operator is asked who will manage the business full time
// (FT): a company buyer its three choices then the AAMA question; an individual
// overseer one five-choice question, AAMA among the choices; a hands-off
// individual the AAMA question only. The wording is FUNNEL_QUESTION_DEFAULTS
// keys; values bind to PrequalBuyerAnswers.involvement / .aamaPath /
// .operatorArrangement.

export const INVOLVEMENT_QUESTION = "How will you be involved day-to-day?";

export const INVOLVEMENT_OPTIONS: ChoiceOption[] = [
  { value: "operator", label: "I'll be the full-time, hands-on operator" },
  { value: "oversight-manager", label: "I'll oversee it and put a full-time manager in place" },
  { value: "hands-off", label: "I'd be hands-off — more of an investment" },
];

/** AAMA follow-up → aamaPath (true when "yes"). The labels are the compiled
 *  defaults of `option-buyer-aama-yes` / `option-buyer-aama-neither`
 *  (FUNNEL_QUESTION_DEFAULTS); the funnel and the call render the published
 *  text. */
export const AAMA_OPTIONS: ChoiceOption[] = [
  { value: "yes", label: "I already run a business in this industry and will install a qualified manager" },
  { value: "no", label: "Neither" },
];

// ── Buyer target step — the industry question, forked by arm ──────────

/**
 * "What industry?" — one label, three helpers, because the sentence beneath it
 * is about the NEXT question and the next question differs by arm.
 *
 * 🔴 THIS COPY IS HERE BECAUSE OF WHAT HAPPENED WHEN IT WAS NOT. The helper
 * was a bare inline literal at the field — "The category — if you already have
 * one business in mind, the next question is for that" — while every one of its
 * neighbours (the business-name labels, the home-city prompt, the reserves pair)
 * lived in this file. So when the funnel grew a second arm and then a third, the
 * ones in here were forked and the inline one was not: it went on telling an
 * INTERNAL buyer that the next question was for the business they had in mind,
 * on the one arm where that question sits ABOVE it and is already answered. A
 * literal nobody finds is a literal nobody updates.
 *
 * 🔴 AND THEN IT HAPPENED A THIRD TIME, ON THE ARM THE FORK LEFT BEHIND. The
 * letter-only helper kept the original sentence on the reasoning that the name
 * question "really is next" there — and it is not, on ANY arm. The business
 * field renders ABOVE this one unconditionally; there is no ordering fork in
 * `BuyerPrequalifyPage`, so the forward-pointer was wrong on all three arms and
 * merely LOOKED right on the one whose sentence nobody had to re-read. Forking
 * a literal is not the same as re-deriving what it claims.
 *
 * All three helpers now describe their own field instead of pointing at another
 * one. That is deliberate beyond tidiness: a sentence naming a position is
 * guaranteed to go stale on the next reshuffle — the same rule
 * `SIZING_METER_PENDING_NOTE` carries about step ordinals. A helper that
 * describes its own field survives a reshuffle; one that points at a neighbour
 * has to be re-derived every time, and demonstrably was not.
 *
 * What the letter-only arm says instead is TRUE and checkable: the free-text
 * industry is rendered on the buyer letter, through `hero-sector-line`. It
 * sizes NOTHING — the engine's industry factor reads `industryFit`, a separate
 * rung asked further down, never this box — so no helper here may imply that
 * typing a trade moves the figure.
 */
export const INDUSTRY_QUESTION = "What industry or trade are you looking at?";
export const INDUSTRY_PLACEHOLDER = "e.g. Plumbing, HVAC, landscaping…";

export const INDUSTRY_HELPER_LETTER_ONLY =
  "The category you’re shopping in — it appears on your letter.";
export const INDUSTRY_HELPER_DEAL =
  "The category that business trades in. It reaches the consultant who prices "
  + "your Deal Sheet.";
export const INDUSTRY_HELPER_INTERNAL =
  "The category the business trades in — the one you’re buying into.";

// ── Operating geography (§B) ────────────────────────────────────────────

/** Home-city prompt on the target step — anchors the "Near me" descriptor. */
export const HOME_CITY_LABEL = "Where are you based?";
export const HOME_CITY_HELPER = "We use this to scope where you'd operate — it stays on your letter.";
export const HOME_CITY_PLACEHOLDER = "City, ST";

/** Values bind to PrequalBuyerAnswers.operatingGeography.mode. "Near me" is a
 *  fixed ~50-mile descriptor in v1 — no radius input, no slider. */
export const GEOGRAPHY_OPTIONS: ChoiceOption[] = [
  { value: "near", label: "Near me" },
  { value: "region", label: "Anywhere in my state / region" },
  { value: "anywhere", label: "Anywhere in the U.S. — I'll relocate" },
];

/** §3 first-timer soft-cap note — rendered near the sizing figure when the
 *  engine result carries `firstTimerCapBound`, i.e. the cap actually pulled the
 *  headline or the band top down. NOT `firstTimerCapApplied`, which is true of
 *  every first-timer whether or not anything was capped: this sentence claims a
 *  figure was held back, so it may only render where one was. */
export const FIRST_TIMER_CAP_NOTE =
  "Capped for first-time owners — expandable with an experienced operator or partner. The Phoenix team can discuss.";

/**
 * The first step’s heading.
 *
 * ⚠️ THE ONE BUYER STEP TITLE THAT IS NOT SERVER-EDITABLE, and deliberately so
 * rather than by omission. Every other title comes from the `funnel-copy`
 * document key via `PrequalFunnelMarketingCopy`, whose keys are pinned against a
 * JSON fixture shared with the .NET test project — so a seventh
 * `stepTitleBuyer*` key is a change on the C# side, in that fixture, and in the
 * copy store’s defaults, none of which belong to a funnel restructure. The
 * compiled-in default is what ships until somebody makes that change on purpose.
 */
export const STEP_TITLE_BUYER_START = "What do you need from Phoenix?";

/**
 * The down-payment sentence the buyer reads on the review step (M36) and the
 * internal-buyer result screen (M32) — C7 in the second person, verbatim from the
 * approval of the 1 November 2026 alignment (§2). Phoenix sets no minimum; the
 * down payment is a term the buyer negotiates with the seller.
 */
export const DOWN_PAYMENT_SECOND_PERSON =
  "Phoenix sets no minimum down payment, and no down-payment funds flow through "
  + "Phoenix. What you put down is a term you negotiate with the seller.";

/**
 * The review step’s recap of the deal the buyer named, and what we assume about
 * their cash.
 *
 * 🔴 THE ASSUMPTION IS SHOWN BACK, NEVER ASKED. The funnel used to carry a
 * "Cash at closing (optional)" field beside the asking price. It has been
 * retired: Phoenix asks no down payment and sets no minimum, so the question
 * invited a buyer to nominate a figure against a requirement that does not
 * exist — and a blank box beneath two required ones reads as an omission rather
 * than as an answer. The capital step already asks what they have. All of it is
 * treated as going to the seller at closing, and this sentence says so in the
 * buyer’s own figure so the assumption is visible rather than silent.
 *
 * `{cash}` is substituted by the caller with the buyer’s own formatted total.
 * A buyer who named nothing gets {@link REVIEW_CASH_ASSUMPTION_NONE} instead —
 * the same assumption is still true of them, and "We’ll assume the $0 you told
 * us about" is not a sentence to show anybody.
 */
export const REVIEW_CASH_ASSUMPTION_PREFIX = "We’ll assume the ";
export const REVIEW_CASH_ASSUMPTION_SUFFIX =
  " you told us about goes to the seller at closing. " + DOWN_PAYMENT_SECOND_PERSON;
export const REVIEW_CASH_ASSUMPTION_NONE =
  "Whatever you put in goes to the seller at closing. " + DOWN_PAYMENT_SECOND_PERSON;

/** The review step’s recap heading, shown on the two arms that named a deal. */
export const REVIEW_DEAL_RECAP_TITLE = "The deal you told us about";

/** What the recap says when the buyer is buying into a business they are already
 *  part of — the arm whose qualification comes from the business rather than
 *  from them, which is worth restating where they are about to submit. */
export const REVIEW_DEAL_RECAP_NOTE_INTERNAL =
  "You’re buying into a business you’re already part of, so we size this on the "
  + "business’s own cash flow rather than on your savings.";

/** The buyer’s optional free-text prompt. It had its own STEP until the funnel
 *  restructure; it now sits on the review step, above the contact fields — one
 *  optional box never justified a sixth of the progress bar, and a buyer who
 *  skipped it still had to press Continue past it. */
export const GOALS_HELPER =
  "A sentence or two about what you're hoping to do — your timeline, the industries you're exploring, and what would make this a win. This shapes the guidance in your documents and how the Phoenix team follows up.";

// ── Seller option catalogs ──────────────────────────────────────────────

// 🔴 RETIRED in prequal-v4: DOWN_PAYMENT_BANDS, DOWN_PAYMENT_PERCENT_LABEL and
// DOWN_PAYMENT_AMOUNT_LABEL.
//
// Phoenix requires no downpayment, sets no minimum, and no downpayment funds
// flow through Phoenix — it is negotiated between the buyer and the seller. A
// banded picker defaulting to "5% (program typical)" asserted all three of
// those things were otherwise, and the "program typical" label read as guidance
// Phoenix does not give. One optional dollar field replaces both inputs, so
// there is also no longer a mode to record or a nearest band to snap a chip to.

/** Left VERBATIM: the seller does still ask a down payment of their buyer, and
 *  the sentence is referenced by the funnel render test. What changed beneath it
 *  is that the answer is optional and may be zero. */
export const DOWN_PAYMENT_QUESTION = "What down payment will you ask of a buyer?";

export const DOWN_PAYMENT_HELPER =
  "Optional — leave it blank or enter zero if you are not asking for one. Phoenix requires no down payment and sets no minimum; this is between you and your buyer, and whatever you agree is paid directly to you rather than through Phoenix. A higher requirement means more cash at closing but narrows the pool of qualified buyers.";

/** Shown under the dollar input once there is a range to measure against, so a
 *  seller can see what their figure works out to as a share of the price. The
 *  percentage is DERIVED for display and is never an input. */
export const DOWN_PAYMENT_AMOUNT_HELPER =
  "Whatever you enter is added to the financed note to arrive at the price, so the price moves with it.";

export const OWNER_HOURS_BANDS: ChoiceOption[] = [
  { value: "under-20", label: "Under 20 hrs/week" },
  { value: "20-40", label: "20–40 hrs/week" },
  { value: "40-60", label: "40–60 hrs/week" },
  { value: "60-plus", label: "60+ hrs/week" },
];

export const ABSENCE_OPTIONS: ChoiceOption[] = [
  { value: "runs-fine", label: "It runs fine without me" },
  { value: "some-issues", label: "Some issues would come up" },
  { value: "stalls", label: "Things would stall" },
];

export const REVENUE_DRIVEN_BANDS: ChoiceOption[] = [
  { value: "under-25", label: "Under 25%" },
  { value: "25-50", label: "25–50%" },
  { value: "over-50", label: "Over 50%" },
];

export const BOOKS_OPTIONS: ChoiceOption[] = [
  { value: "cpa-reviewed", label: "CPA-reviewed" },
  { value: "accountant-prepared", label: "Accountant-prepared" },
  { value: "informal", label: "Informal / owner-kept" },
];

export const YES_NO_OPTIONS: ChoiceOption[] = [
  { value: "no", label: "No" },
  { value: "yes", label: "Yes" },
];

export const TRANSITION_OPTIONS: ChoiceOption[] = [
  { value: "full-transition", label: "Yes — a full hands-on transition" },
  { value: "limited-transition", label: "A limited transition period" },
  { value: "minimal", label: "Minimal — mostly documentation" },
];

// ── Shared copy ─────────────────────────────────────────────────────────

// Finalize consent + fine print — path-dynamic (buyer/seller). Phoenix-owned
// LEGAL copy: NOT partner-overridable. The share-with-a-certified-representative
// clause is affirmative PII-sharing consent → it lives IN the checkbox.
//
// ── CORRECTION ───────────────────────────────────────────────────────────
//
// This comment used to say these strings were "deliberately NOT routed through
// the PrequalDocumentCopy system (that governs only server-rendered docs/email
// and has no non-partner-overridable block class)". The parenthetical was wrong
// and the conclusion did not follow from it.
//
// `PrequalDocumentCopyRules.IsPhoenixTierOnly` provides exactly a
// non-partner-overridable block — admin-editable at the Phoenix Default set,
// refused for every partner set, enforced at resolution AND in the editor — and
// was already in production use for the analysis documents' privacy markers. The
// sentence was literally true about the `PrequalDocumentBlockClass` enum (which
// has only Editable and Structural) and false about the capability, and reading
// it cost a build cycle: it argues for routing around the one mechanism that
// already did the job.
//
// These six blocks are now REGISTERED in that system under the
// `funnel-fine-print` document key and marked Phoenix-tier-only, so staff can
// edit them without a deploy and no partner can diverge from them.
//
// ── WHY THESE CONSTANTS STILL EXIST ──────────────────────────────────────
//
// They are the FALLBACK, not dead code — the client-side mirror of the same
// code-default-underneath-the-store shape the server uses. `useFunnelCopy()`
// starts here and overlays the server's published copy when it arrives, so a
// slow, rate-limited or failed fetch degrades to these words instead of painting
// a checkbox with no consent sentence beside it. An empty override table
// therefore renders byte-identically to before this feature existed.
//
// 🔴 They MUST stay byte-identical to `PrequalDocumentCopyDefaults`'
// funnel-fine-print blocks. Nothing imports across the language boundary, so
// `PrequalFunnelCopyParityTests` pins the two sides against a shared fixture —
// the same guard `PrequalCrossLanguageParityTests` provides for the sizing
// engine, and for the same reason: a one-sided edit here would ship a funnel
// whose consent line disagreed with the text recorded against the submission.

export const CONSENT_LINE_SELLER =
  "I agree that Phoenix Capital Solutions, LLC may store the information I provided, contact me about my prequalification, and share it with a certified seller representative. Figures are indicative, based on unverified self-reported information, and are not a commitment to lend.";

export const CONSENT_LINE_BUYER =
  "I agree that Phoenix Capital Solutions, LLC may store the information I provided, contact me about my prequalification, and share it with a certified buyer representative. Figures are indicative, based on unverified self-reported information, and are not a commitment to lend.";

// 🔴 Must stay byte-identical to the C# default AND to
// tests/fixtures/prequal-funnel-copy.json. The consent snapshot is the legal
// record of what an applicant accepted and THIS is what they actually read, so a
// one-sided edit makes the record describe a screen nobody was shown.
//
// 1 November 2026 (approval §3, M38): both lines open with the one generic fee
// sentence and close on "Eligibility is subject to underwriting and to the note
// holder's decision." They are NEW copy keys on the server (fine-print-buyer-v2 /
// fine-print-seller-v2, D14): the retired keys stated a fee sentence and a
// Phoenix-discretion eligibility claim that are no longer true. The fee
// statements themselves live on the letters, deal sheets, document 3 and Deal
// Explorer (D6); only the funnel fine print is generic. The seller line now
// differs from the buyer's only in naming the seller's representative.
//
// 🔴 Both roles' fine print ends with the REQUIRED STATUS LANGUAGE, in full —
// the `funnel-fine-print` key carries no banned-term list, so unlike the letters
// and the buyer sheet it can ship the instrument-class clause verbatim. Held as
// one const on the server (`PrequalDocumentCopyDefaults.StatusLanguageNoteHolderEligibility`,
// the status language with the note-holder eligibility sentence) and therefore
// literal here; if that const is ever reworded, all THREE copies move together or
// the parity ratchet fails on whichever two were not edited.
export const FINE_PRINT_SELLER =
  "Program fees will apply and are covered in program documents. Phoenix Capital Solutions administers financing and does not work directly with buyers or sellers — you'll be represented by a seller's representative. Phoenix Capital Solutions, LLC is not a registered investment adviser, broker-dealer, lender, or licensed loan servicer. It acts as an administrator of seller-financed business acquisition notes, with administration services provided with licensed partners where required. This material is informational and is not an offer or solicitation to purchase or sell any security or investment product. Timeframes are illustrative and reflect an objective, not a commitment. Eligibility is subject to underwriting and to the note holder's decision.";

export const FINE_PRINT_BUYER =
  "Program fees will apply and are covered in program documents. Phoenix Capital Solutions administers financing and does not work directly with buyers or sellers — you'll be represented by a buyer's representative. Phoenix Capital Solutions, LLC is not a registered investment adviser, broker-dealer, lender, or licensed loan servicer. It acts as an administrator of seller-financed business acquisition notes, with administration services provided with licensed partners where required. This material is informational and is not an offer or solicitation to purchase or sell any security or investment product. Timeframes are illustrative and reflect an objective, not a commitment. Eligibility is subject to underwriting and to the note holder's decision.";

export const INDICATIVE_LABEL =
  "Indicative — subject to verification, not a commitment to lend.";

/** §2's free cash flow, trimmed (ruling Q7): the helper on every free-cash-flow
 *  input the funnels and Deal Explorer ask (M34, M37, M39). The buyer's two
 *  helpers (`helper-buyer-free-cash-flow-*`) open with the same sentence. */
export const FREE_CASH_FLOW_DEFINITION =
  "Earnings before interest, income taxes, depreciation, amortization and all owner compensation, less a fair-market salary for the owner's role and annual maintenance capital expenditure.";

/** C5 — rate and structure, verbatim from the program documents (§2). The
 *  capital-arm result hero states it under the figure (M33, matching the letter). */
export const PROGRAM_RATE_AND_STRUCTURE =
  "10% fixed — the Current Program Rate, subject to change and to the Note Holder's final determination — on a 10- or 20-year amortization the buyer chooses, with a balloon at the end of Year 8.";

/** C6 — the sizing rule, both sentences, verbatim (§2). The seller and
 *  internal-buyer result heroes state it under the two schedules (M31, M32). */
export const PROGRAM_SIZING_RULE =
  "Financing is generally up to five times free cash flow on a 20-year schedule, and less on a 10-year schedule: the lower of 5× free cash flow and what the business can carry, with a margin, on the payment schedule the buyer chooses. The limit applies to the amount financed, not to the sale price.";

/** The live guide's one-line C6 (M28, M29): the meter shows the 20-year figure,
 *  and this says the 10-year schedule finances less (D5). */
export const SIZING_METER_SCHEDULE_NOTE =
  "Up to five times free cash flow on a 20-year schedule, and less on a 10-year schedule.";

/** The per-schedule below-minimum treatment (D2, ruling Q5 (a)): the schedule's
 *  column carries this sentence in place of its figure. The caller formats the
 *  minimum from `PREQUAL_PROGRAM_TERMS.minAmountFinanced`. */
export function belowProgramMinimumOnSchedule(minimumFinanced: string): string {
  return `Below the ${minimumFinanced} program minimum on this schedule.`;
}

/* ── The program terms in the two constants below (1 November 2026) ─────
 *
 * Final text, verbatim, from the approval of the 1 November 2026 alignment (§2):
 * M26 is the landing page's terms line, M25 the subhead above every funnel step.
 * They state C5 (rate and structure), C6 (sizing — the 20-year schedule and "less
 * on a 10-year schedule"), C12 (the program range) and the down payment's 4–6%
 * planning range with the note-holder disclaimer. No coverage ratio appears: the
 * 1.50x sizing coverage and the 1.35x origination floor are staff-only (D2), and
 * "Program terms are set by Phoenix" is gone — the note holder makes the final
 * determination, not Phoenix.
 *
 * The figures are literal prose here; tests/fixtures/prequal-funnel-copy.json ›
 * programTermsFigures names each one, the .NET half pins every figure to its
 * PrequalProgramTerms constant, and the TypeScript half pins that both strings
 * still contain it.
 */

export const PROGRAM_TERMS_LINE =
  "10% fixed — the Current Program Rate, subject to change and to the Note Holder's final determination — on a 10- or 20-year amortization the buyer chooses, with a balloon at the end of Year 8. Financing is generally up to five times free cash flow on a 20-year schedule, and less on a 10-year schedule: the lower of 5× free cash flow and what the business can carry, with a margin, on the payment schedule the buyer chooses. The limit applies to the amount financed, not to the sale price. The program finances $250,000 to $10 million per acquisition. Down payment is typically 4–6% (planning midpoint 5%). This is an indicative range note holders typically require. The down payment is negotiated with and decided by the note holder; Phoenix sets no minimum.";

/** Funnel-page subhead (M25), on every buyer and seller funnel step. */
export const PROGRAM_TERMS_SUBHEAD =
  "10% fixed — the Current Program Rate, subject to change and to the Note Holder's final determination — on a 10- or 20-year amortization the buyer chooses, with a balloon at the end of Year 8. Financing is generally up to five times free cash flow on a 20-year schedule, and less on a 10-year schedule: the lower of 5× free cash flow and what the business can carry, with a margin, on the payment schedule the buyer chooses. The program finances $250,000 to $10 million per acquisition. Down payment is typically 4–6% (planning midpoint 5%). Indicative only — the note holder decides; Phoenix sets no minimum.";

/** Gold overline above every funnel H1 (§4.2; CSS-uppercase renders caps). */
export const PROGRAM_OVERLINE = "Phoenix Business Acquisition Program";

/**
 * ASSISTED mode only — the decision-22 replacement, shown on the first step.
 *
 * PrequalSaveProgressCard suppresses itself when somebody else is driving,
 * because the invite behind an assisted draft is minted with its email
 * deliberately suppressed and promising the applicant a link back would promise
 * something nobody sends. Silence would be wrong too, so this says what IS
 * true. Lives here rather than beside the assist surface because both funnel
 * pages render it and the surface imports THEM — the other direction is an
 * import cycle.
 *
 * The resend claim is made because it is TRUE and was verified rather than
 * assumed: the in-office invite is minted with its email suppressed but left in
 * the ordinary `Started` state, resend refuses only completed and revoked
 * invites, it re-mails the link it already holds rather than reminting one, and
 * the Resend action renders for it on both the staff list and the portal tab.
 * If any of that stops being true, this sentence has to go — promising somebody
 * a link nobody will send is worse than saying nothing.
 */
export const ASSIST_SAVE_NOTE =
  "Answers are saved as you go, so you can stop and finish this later — it stays "
  + "on your in-progress list. If they would rather finish it themselves, resend "
  + "their invitation and they will get the link by email.";

export const ADMINISTRATOR_LINE =
  "Phoenix Capital acts as the Loan/Note Administrator for every transaction in this program.";

/** The certification a Buyer Consultant gives on a telephone-call
 *  prequalification from the buyer record (block `call-certification-buyer`).
 *  The funnel never renders it — the staff call screen does. Pinned byte for byte
 *  to the C# default by tests/fixtures/prequal-funnel-copy.json. */
export const CALL_CERTIFICATION_BUYER =
  "I read the consent statement above to the buyer on a telephone call, and the buyer "
  + "agreed to it verbally. I entered the buyer's answers as they gave them to me.";

// ── Funnel MARKETING copy (document key `funnel-copy`) ───────────────────
//
// The trust band, the landing hero and role fork, and every step's question and
// hint. Same shape as the fine print above and for the same reasons: these are the
// FALLBACK, not dead code — `useFunnelCopy()` starts here and overlays the server's
// published copy when it arrives, so a slow, rate-limited or failed fetch degrades
// to today's wording rather than painting an empty headline.
//
// 🔴 They MUST stay byte-identical to `PrequalDocumentCopyDefaults`' funnel-copy
// blocks. Nothing imports across the language boundary, so
// `PrequalFunnelCopyParityTests` pins the two sides against a shared fixture. The
// U+2014 em dashes, the U+00B7 in "Free · no account needed", and the U+2019 curly
// apostrophes in "I’m" and "What’s" are the obvious way to break that.
//
// These are gathered here from THREE places they used to live: PortalShell's trust
// band, PrequalifyLandingPage's hero consts and fork JSX, and the two step-meta
// maps. Those sites now read the resolved copy; these constants are what they fall
// back to.
//
// 🔴 HERO_HEADLINE / HERO_SUBCOPY are the PHOENIX tier, not the last word. A partner
// brand's `heroHeadline` / `heroSubcopy` sit UPSTREAM and win when set:
//
//     PartnerBrandSkin.heroHeadline → funnel-copy published block → this constant
//        (partner tier)               (Phoenix tier)                 (fallback)
//
// PROGRAM_OVERLINE is declared above (it predates this key) and is reused here
// rather than duplicated.

export const TRUST_SECURE_PORTAL =
  "Official Phoenix Capital secure portal — encrypted in transit & at rest";
export const TRUST_DURATION = "~3 minutes";
export const TRUST_NO_CREDIT_PULL = "No credit pull";
export const TRUST_FREE = "Free · no account needed";

export const HERO_HEADLINE = "See what you could qualify for — in minutes";
export const HERO_SUBCOPY =
  "A few questions, an instant indicative figure, and your documents at the end. " +
  "Nothing is verified yet and nothing is committed — this is a planning tool.";
export const FORK_QUESTION = "Are you buying or selling a business?";
export const FORK_TITLE_BUYER = "I’m buying a business";
export const FORK_BODY_BUYER =
  "Find out how much business you could acquire with the cash you have — " +
  "one simple loan through Phoenix — no bank needed.";
export const FORK_TITLE_SELLER = "I’m selling my business";
export const FORK_BODY_SELLER =
  "See the price range your cash flow can support for a structured sale — " +
  "and what your monthly income could look like.";

export const STEP_TITLE_BUYER_TARGET = "What kind of business are you looking for?";
export const STEP_TITLE_BUYER_CAPITAL = "How much can you put toward the purchase?";
export const STEP_HINT_BUYER_CAPITAL =
  "Your cash and other assets drive the indicative figure on the right — " +
  "it updates as you type.";
export const STEP_TITLE_BUYER_EXPERIENCE = "What’s your ownership background?";
export const STEP_TITLE_BUYER_FIT_CHECK = "The fit check.";
export const STEP_TITLE_BUYER_GOALS = "What are you hoping to accomplish?";
export const STEP_TITLE_BUYER_FINALIZE = "Finalize to receive your documents";
export const STEP_HINT_BUYER_FINALIZE =
  "Who the documents are made out to — and where they go.";

export const STEP_TITLE_SELLER_BUSINESS = "Tell us about the business.";
export const STEP_TITLE_SELLER_FIT_CHECK = "The fit check.";
export const STEP_TITLE_SELLER_NUMBERS = "What do the numbers look like?";
export const STEP_HINT_SELLER_NUMBERS =
  "Cash flow drives the indicative range on the right — it updates as you type.";
export const STEP_TITLE_SELLER_OWNER_RELIANCE = "How much does the business rely on you?";
export const STEP_TITLE_SELLER_RECORDS = "How clean are the records?";
export const STEP_TITLE_SELLER_EXIT = "What does your exit look like?";
export const STEP_TITLE_SELLER_FINALIZE = "Finalize to receive your documents";
export const STEP_HINT_SELLER_FINALIZE =
  "Who the documents are made out to — and where they go.";

/** The compiled-in default for every funnel MARKETING block, in the wire shape.
 *  Declared before FUNNEL_COPY_FALLBACK, which nests it — these are `const`, so
 *  they are not hoisted. */
// ── Company & sponsor buyers (owner brief 2026-10-08, D1-D4) ─────────────
//
// The funnel's FIRST admin-editable question, option and label copy
// (`funnel-copy` keys `question-buyer-type` … `helper-buyer-company-about`). These
// are the compiled-in fallbacks; the published text arrives through
// `useFunnelCopy().marketing`. The store takes no tokens, so options say "the
// acquiring company" rather than its name. The full-time operator is
// "designated", never "named" (ruling Q4). Pinned byte for byte to the C#
// defaults by tests/fixtures/prequal-funnel-marketing.json. The full-time-manager
// question and helper that used to sit here are FUNNEL_QUESTION_DEFAULTS keys now
// (`question-buyer-full-time-manager`, `helper-buyer-full-time-manager`).
export const QUESTION_BUYER_TYPE =
  "Who is buying?";
export const OPTION_BUYER_TYPE_INDIVIDUAL =
  "Me personally";
export const OPTION_BUYER_TYPE_COMPANY =
  "A company I own or lead";
export const OPTION_BUYER_TYPE_FUND =
  "An investment group or fund I represent";
export const LABEL_BUYER_CAPITAL_COMPANY =
  "Expected equity contribution from the acquiring company";
export const HELPER_BUYER_CAPITAL_COMPANY =
  "What the acquiring company expects to put toward the purchase. It is typically 4–6% of the purchase price (planning midpoint 5%), paid to the seller. This is an indicative range note holders typically require. The down payment is negotiated with and decided by the note holder; Phoenix sets no minimum.";
export const STEP_HINT_BUYER_CAPITAL_COMPANY =
  "The acquiring company’s expected equity contribution drives the indicative figure on the right — it updates as you type.";
export const QUESTION_BUYER_EQUITY_SOURCE =
  "Where will the equity contribution come from?";
export const OPTION_BUYER_EQUITY_SOURCE_ON_HAND =
  "Company funds on hand";
export const OPTION_BUYER_EQUITY_SOURCE_COMMITTED =
  "Committed by investors or partners";
export const OPTION_BUYER_EQUITY_SOURCE_RAISING =
  "Still being raised (subject to final capitalization)";
export const OPTION_BUYER_OPERATOR_PRINCIPAL =
  "A principal of the acquiring company";
export const OPTION_BUYER_OPERATOR_GENERAL_MANAGER =
  "A general manager we will hire";
export const OPTION_BUYER_OPERATOR_EXISTING_MANAGEMENT =
  "Existing management will stay";
export const LABEL_BUYER_OPERATOR_NAME =
  "Their name";
export const LABEL_BUYER_OPERATOR_NAME_OPTIONAL =
  "Their name (optional)";
export const LABEL_BUYER_COMPANY_NAME =
  "Acquiring company name";
export const LABEL_BUYER_COMPANY_TITLE =
  "Your title (e.g. CEO)";
export const LABEL_BUYER_COMPANY_WEBSITE =
  "Company website (optional)";
export const LABEL_BUYER_COMPANY_ABOUT =
  "About the company (optional)";
export const HELPER_BUYER_COMPANY_ABOUT =
  "Two to four sentences on what the company does.";

export const FUNNEL_MARKETING_FALLBACK: PrequalFunnelMarketingCopy = {
  trustSecurePortal: TRUST_SECURE_PORTAL,
  trustDuration: TRUST_DURATION,
  trustNoCreditPull: TRUST_NO_CREDIT_PULL,
  trustFree: TRUST_FREE,
  programOverline: PROGRAM_OVERLINE,
  heroHeadline: HERO_HEADLINE,
  heroSubcopy: HERO_SUBCOPY,
  forkQuestion: FORK_QUESTION,
  forkTitleBuyer: FORK_TITLE_BUYER,
  forkBodyBuyer: FORK_BODY_BUYER,
  forkTitleSeller: FORK_TITLE_SELLER,
  forkBodySeller: FORK_BODY_SELLER,
  stepTitleBuyerTarget: STEP_TITLE_BUYER_TARGET,
  stepTitleBuyerCapital: STEP_TITLE_BUYER_CAPITAL,
  stepHintBuyerCapital: STEP_HINT_BUYER_CAPITAL,
  stepTitleBuyerExperience: STEP_TITLE_BUYER_EXPERIENCE,
  stepTitleBuyerFitCheck: STEP_TITLE_BUYER_FIT_CHECK,
  stepTitleBuyerGoals: STEP_TITLE_BUYER_GOALS,
  stepTitleBuyerFinalize: STEP_TITLE_BUYER_FINALIZE,
  stepHintBuyerFinalize: STEP_HINT_BUYER_FINALIZE,
  stepTitleSellerBusiness: STEP_TITLE_SELLER_BUSINESS,
  stepTitleSellerFitCheck: STEP_TITLE_SELLER_FIT_CHECK,
  stepTitleSellerNumbers: STEP_TITLE_SELLER_NUMBERS,
  stepHintSellerNumbers: STEP_HINT_SELLER_NUMBERS,
  stepTitleSellerOwnerReliance: STEP_TITLE_SELLER_OWNER_RELIANCE,
  stepTitleSellerRecords: STEP_TITLE_SELLER_RECORDS,
  stepTitleSellerExit: STEP_TITLE_SELLER_EXIT,
  stepTitleSellerFinalize: STEP_TITLE_SELLER_FINALIZE,
  stepHintSellerFinalize: STEP_HINT_SELLER_FINALIZE,
  questionBuyerType: QUESTION_BUYER_TYPE,
  optionBuyerTypeIndividual: OPTION_BUYER_TYPE_INDIVIDUAL,
  optionBuyerTypeCompany: OPTION_BUYER_TYPE_COMPANY,
  optionBuyerTypeFund: OPTION_BUYER_TYPE_FUND,
  labelBuyerCapitalCompany: LABEL_BUYER_CAPITAL_COMPANY,
  helperBuyerCapitalCompany: HELPER_BUYER_CAPITAL_COMPANY,
  stepHintBuyerCapitalCompany: STEP_HINT_BUYER_CAPITAL_COMPANY,
  questionBuyerEquitySource: QUESTION_BUYER_EQUITY_SOURCE,
  optionBuyerEquitySourceOnHand: OPTION_BUYER_EQUITY_SOURCE_ON_HAND,
  optionBuyerEquitySourceCommitted: OPTION_BUYER_EQUITY_SOURCE_COMMITTED,
  optionBuyerEquitySourceRaising: OPTION_BUYER_EQUITY_SOURCE_RAISING,
  optionBuyerOperatorPrincipal: OPTION_BUYER_OPERATOR_PRINCIPAL,
  optionBuyerOperatorGeneralManager: OPTION_BUYER_OPERATOR_GENERAL_MANAGER,
  optionBuyerOperatorExistingManagement: OPTION_BUYER_OPERATOR_EXISTING_MANAGEMENT,
  labelBuyerOperatorName: LABEL_BUYER_OPERATOR_NAME,
  labelBuyerOperatorNameOptional: LABEL_BUYER_OPERATOR_NAME_OPTIONAL,
  labelBuyerCompanyName: LABEL_BUYER_COMPANY_NAME,
  labelBuyerCompanyTitle: LABEL_BUYER_COMPANY_TITLE,
  labelBuyerCompanyWebsite: LABEL_BUYER_COMPANY_WEBSITE,
  labelBuyerCompanyAbout: LABEL_BUYER_COMPANY_ABOUT,
  helperBuyerCompanyAbout: HELPER_BUYER_COMPANY_ABOUT,
};

// ── Funnel QUESTION copy (document key `funnel-copy`, PrequalFunnelQuestionBlocks)
//
// Every buyer question the funnel and the staff call ask — its label, its option
// LABELS (the option VALUES stay coded: they are the wire), its helper and, on
// the eligibility step, each knockout's statement, confirm question, restriction
// explanation and unsure note. One const map keyed by BLOCK KEY, in funnel order,
// rather than one export per string: the server sends the published text of the
// same keys as ONE map (`PrequalFunnelCopy.questions`), so the fallback and the
// wire share a shape and a key type.
//
// Values are the wording the funnel renders today, read from the constants above
// where one exists (never retyped), except the FT rewording of the management
// requirement and its follow-ups and of the geography question (owner-approved
// copy table). Pinned byte for byte to the C# defaults by
// tests/fixtures/prequal-funnel-questions.json.
//
// Out of this map on purpose (Q7): the price-band option labels, the shared
// yes/no/confirm chips, the checklist intro, the start step's title, the
// industry question and the home city.

/** One buyer knockout's text, read from BUYER_KNOCKOUTS. Throws at import when the
 *  knockout or the part is missing, so a renamed key cannot silently default to
 *  an empty question. */
function buyerKnockoutText(
  key: string,
  part: "statement" | "confirm" | "restrict" | "unsure",
): string {
  const q = BUYER_KNOCKOUTS.find((k) => k.key === key);
  const text =
    part === "statement"
      ? q?.statement
      : part === "confirm"
        ? q?.followUp?.followUpQuestion
        : part === "restrict"
          ? q?.restrictExplanation
          : q?.unsureNote;
  if (!text) throw new Error(`No ${part} text for buyer knockout '${key}'`);
  return text;
}

/** One option's label, read from its catalog. Throws at import when absent. */
function optionLabel(options: readonly ChoiceOption[], value: string): string {
  const label = options.find((o) => o.value === value)?.label;
  if (!label) throw new Error(`No option '${value}'`);
  return label;
}

function pathCard(value: string): DocumentPathCard {
  const card = DOCUMENT_PATH_CARDS.find((c) => c.value === value);
  if (!card) throw new Error(`No document path card '${value}'`);
  return card;
}

/** The management requirement, said to anyone who will not run the business
 *  themselves (FT). The company follow-up's helper says the same thing. */
const MANAGED_FULL_TIME_REQUIREMENT =
  "The program requires the business to be managed full time for the first two years, with some offsite work permitted. A designated operator may satisfy this requirement, subject to verification during underwriting.";

export const FUNNEL_QUESTION_DEFAULTS = {
  // ── Start: which documents ───────────────────────────────────────────
  "label-buyer-path-call": "Which documents?",
  "option-buyer-path-letter-only": pathCard(DOCUMENT_PATH_LETTER_ONLY).label,
  "option-buyer-path-letter-only-body": pathCard(DOCUMENT_PATH_LETTER_ONLY).body,
  "option-buyer-path-letter-only-produces": pathCard(DOCUMENT_PATH_LETTER_ONLY).produces,
  "option-buyer-path-letter-and-deal-sheet": pathCard(DOCUMENT_PATH_WITH_DEAL_SHEET).label,
  "option-buyer-path-letter-and-deal-sheet-body": pathCard(DOCUMENT_PATH_WITH_DEAL_SHEET).body,
  "option-buyer-path-letter-and-deal-sheet-produces": pathCard(DOCUMENT_PATH_WITH_DEAL_SHEET).produces,
  "option-buyer-path-internal-buyer": pathCard(DOCUMENT_PATH_INTERNAL).label,
  "option-buyer-path-internal-buyer-body": pathCard(DOCUMENT_PATH_INTERNAL).body,
  "option-buyer-path-internal-buyer-produces": pathCard(DOCUMENT_PATH_INTERNAL).produces,

  // ── Target ───────────────────────────────────────────────────────────
  "question-buyer-internal-relationship": "How are you connected to the business?",
  "option-buyer-internal-relationship-family-member":
    optionLabel(INTERNAL_RELATIONSHIP_OPTIONS, "FamilyMember"),
  "option-buyer-internal-relationship-employee-or-manager":
    optionLabel(INTERNAL_RELATIONSHIP_OPTIONS, "EmployeeOrManager"),
  "option-buyer-internal-relationship-partner-or-co-owner":
    optionLabel(INTERNAL_RELATIONSHIP_OPTIONS, "PartnerOrCoOwner"),
  "label-buyer-internal-years": "How long have you worked there? (optional)",
  "helper-buyer-internal-years":
    "In years. There’s no minimum — it just helps us understand the business.",
  "label-buyer-target-business-internal": TARGET_BUSINESS_LABEL_INTERNAL,
  "label-buyer-target-business-deal": TARGET_BUSINESS_LABEL_DEAL,
  "label-buyer-target-business-letter-only": TARGET_BUSINESS_LABEL_LETTER_ONLY,
  "helper-buyer-target-business-internal": TARGET_BUSINESS_HELPER_INTERNAL,
  "helper-buyer-target-business-deal": TARGET_BUSINESS_HELPER_DEAL,
  "helper-buyer-target-business-letter-only": TARGET_BUSINESS_HELPER_LETTER_ONLY,
  "question-buyer-price-band": "Roughly what price range?",
  "label-buyer-internal-co-buyers": "Is anyone buying in with you? (optional)",
  "helper-buyer-internal-co-buyers":
    "How many people, not who — we’ll take the names later if there are any.",
  "label-buyer-asking-price": "What is the asking price?",
  "helper-buyer-asking-price-internal":
    "What you and the owner have discussed. A working number is fine.",
  "helper-buyer-asking-price-deal":
    "The price on the listing, or what you and the seller have discussed.",
  "label-buyer-cash-flow-internal": "Annual free cash flow",
  "label-buyer-cash-flow-deal": "Annual free cash flow (optional)",
  "helper-buyer-free-cash-flow-internal":
    "Earnings before interest, income taxes, depreciation, amortization and all owner compensation, less a fair-market salary for the owner's role and annual maintenance capital expenditure. This is what qualifies the purchase — the business’s own earnings, not your savings.",
  "helper-buyer-free-cash-flow-deal":
    "Earnings before interest, income taxes, depreciation, amortization and all owner compensation, less a fair-market salary for the owner's role and annual maintenance capital expenditure. If you have it, your Deal Sheet shows what this business can support on the 20- and 10-year schedules.",
  "label-buyer-close-date": "When do you expect to close? (optional)",
  "helper-buyer-close-date":
    "A best guess is fine — it prints on your Deal Sheet and changes no figure.",

  // ── Capital ──────────────────────────────────────────────────────────
  "label-buyer-cash": "Cash available for the down payment",
  "helper-buyer-cash-note-holder":
    "Down payment is typically 4–6% of the purchase price (planning midpoint 5%), paid directly to the seller. This is an indicative range note holders typically require. The down payment is negotiated with and decided by the note holder; Phoenix sets no minimum.",
  // The call's own toggle: the funnel has no "won't share" answer.
  "label-buyer-cash-wont-share": "They won't share a figure",
  "heading-buyer-other-assets": "Other assets toward your down payment",
  "option-buyer-other-asset-real-estate-equity": "Real estate equity",
  "option-buyer-other-asset-securities": "Publicly traded stock or securities",
  "option-buyer-other-asset-business-equity": "Business equity or ownership stake",
  "option-buyer-other-asset-retirement-accounts":
    "Retirement or investment accounts (401(k), IRA, and similar)",
  "option-buyer-other-asset-other-assets": "Other",
  "label-buyer-other-assets-note": "Anything else about how you’re funding your down payment?",
  "helper-buyer-other-assets-note": "Optional — one note covers everything you ticked above.",
  "label-buyer-reserves": RESERVES_AMOUNT_LABEL,
  "helper-buyer-reserves": RESERVES_AMOUNT_HELPER,
  "question-buyer-reserves-months": RESERVES_BAND_QUESTION,
  "option-buyer-reserves-under-3mo": optionLabel(RESERVES_BANDS, "under-3mo"),
  "option-buyer-reserves-3-6mo": optionLabel(RESERVES_BANDS, "3-6mo"),
  "option-buyer-reserves-6mo-plus": optionLabel(RESERVES_BANDS, "6mo-plus"),

  // ── Background ───────────────────────────────────────────────────────
  "question-buyer-ownership": "Have you owned a business before?",
  "option-buyer-ownership-never-owned": optionLabel(OWNERSHIP_LADDER, "never-owned"),
  "option-buyer-ownership-owned-smaller": optionLabel(OWNERSHIP_LADDER, "owned-smaller"),
  "option-buyer-ownership-owned-comparable": optionLabel(OWNERSHIP_LADDER, "owned-comparable"),
  "option-buyer-ownership-owned-multiple": optionLabel(OWNERSHIP_LADDER, "owned-multiple"),
  // Call only: the interview derivation's one-tap follow-up.
  "question-buyer-owns-now": "Do they own it now?",
  "question-buyer-management": "What’s your management experience?",
  "option-buyer-management-none": optionLabel(MANAGEMENT_LADDER, "none"),
  "option-buyer-management-small-team": optionLabel(MANAGEMENT_LADDER, "small-team"),
  "option-buyer-management-department-pnl": optionLabel(MANAGEMENT_LADDER, "department-pnl"),
  "option-buyer-management-executive-pnl": optionLabel(MANAGEMENT_LADDER, "executive-pnl"),
  "question-buyer-industry-fit": "How close is this industry to your background?",
  "option-buyer-industry-fit-new": optionLabel(INDUSTRY_LADDER, "new"),
  "option-buyer-industry-fit-adjacent": optionLabel(INDUSTRY_LADDER, "adjacent"),
  "option-buyer-industry-fit-direct": optionLabel(INDUSTRY_LADDER, "direct"),
  "option-buyer-industry-fit-direct-5plus": optionLabel(INDUSTRY_LADDER, "direct-5plus"),
  // Call only: the interview derivation's one-tap follow-up.
  "question-buyer-researched-industry": "Have they researched it?",
  "question-buyer-involvement": INVOLVEMENT_QUESTION,
  "option-buyer-involvement-operator": optionLabel(INVOLVEMENT_OPTIONS, "operator"),
  "option-buyer-involvement-oversight-manager": optionLabel(INVOLVEMENT_OPTIONS, "oversight-manager"),
  "option-buyer-involvement-hands-off": optionLabel(INVOLVEMENT_OPTIONS, "hands-off"),
  // FT: managed full time, some offsite work permitted; on-site presence is
  // required nowhere. "Designated", never "named" (ruling Q4).
  "requirement-buyer-managed-full-time": MANAGED_FULL_TIME_REQUIREMENT,
  "requirement-buyer-managed-full-time-hands-off":
    "The program requires the business to be managed full time for the first two years, with some offsite work permitted. For an owner who will be hands-off, this is met only by already running a business in this industry and installing a qualified manager, subject to verification during underwriting.",
  "question-buyer-full-time-manager": "Who will manage the business full time?",
  "helper-buyer-full-time-manager": MANAGED_FULL_TIME_REQUIREMENT,
  "option-buyer-manager-gm-individual": "A general manager I will hire",
  "option-buyer-manager-existing-individual": "Existing management will stay",
  "option-buyer-manager-designated-person": "Another person I designate",
  "option-buyer-manager-aama": "I already run a business in this industry and will install a manager",
  "option-buyer-manager-undecided": "Not decided yet",
  "question-buyer-aama": "Which fits you?",
  "option-buyer-aama-yes": optionLabel(AAMA_OPTIONS, "yes"),
  "option-buyer-aama-neither": optionLabel(AAMA_OPTIONS, "no"),
  "question-buyer-operating-geography": "Where are you open to operating the business?",
  "option-buyer-geography-near": optionLabel(GEOGRAPHY_OPTIONS, "near"),
  "option-buyer-geography-region": optionLabel(GEOGRAPHY_OPTIONS, "region"),
  "option-buyer-geography-anywhere": optionLabel(GEOGRAPHY_OPTIONS, "anywhere"),

  // ── Eligibility: the seven buyer knockouts ───────────────────────────
  "knockout-statement-buyer-bankruptcy": buyerKnockoutText("buyer-bankruptcy", "statement"),
  "knockout-confirm-buyer-bankruptcy": buyerKnockoutText("buyer-bankruptcy", "confirm"),
  "knockout-restrict-buyer-bankruptcy": buyerKnockoutText("buyer-bankruptcy", "restrict"),
  "knockout-unsure-buyer-bankruptcy": buyerKnockoutText("buyer-bankruptcy", "unsure"),
  "knockout-statement-buyer-ofac": buyerKnockoutText("buyer-ofac", "statement"),
  "knockout-confirm-buyer-ofac": buyerKnockoutText("buyer-ofac", "confirm"),
  "knockout-restrict-buyer-ofac": buyerKnockoutText("buyer-ofac", "restrict"),
  "knockout-unsure-buyer-ofac": buyerKnockoutText("buyer-ofac", "unsure"),
  "knockout-statement-buyer-felony": buyerKnockoutText("buyer-felony", "statement"),
  "knockout-confirm-buyer-felony": buyerKnockoutText("buyer-felony", "confirm"),
  "knockout-restrict-buyer-felony": buyerKnockoutText("buyer-felony", "restrict"),
  "knockout-unsure-buyer-felony": buyerKnockoutText("buyer-felony", "unsure"),
  "knockout-statement-buyer-other-debt": buyerKnockoutText("buyer-other-debt", "statement"),
  "knockout-confirm-buyer-other-debt": buyerKnockoutText("buyer-other-debt", "confirm"),
  "knockout-restrict-buyer-other-debt": buyerKnockoutText("buyer-other-debt", "restrict"),
  "knockout-unsure-buyer-other-debt": buyerKnockoutText("buyer-other-debt", "unsure"),
  "knockout-statement-buyer-no-guarantee": buyerKnockoutText("buyer-no-guarantee", "statement"),
  "knockout-confirm-buyer-no-guarantee": buyerKnockoutText("buyer-no-guarantee", "confirm"),
  "knockout-restrict-buyer-no-guarantee": buyerKnockoutText("buyer-no-guarantee", "restrict"),
  "knockout-unsure-buyer-no-guarantee": buyerKnockoutText("buyer-no-guarantee", "unsure"),
  // No unsure note: the excluded-industry knockout has none.
  "knockout-statement-buyer-excluded-industry": buyerKnockoutText("buyer-excluded-industry", "statement"),
  "knockout-confirm-buyer-excluded-industry": buyerKnockoutText("buyer-excluded-industry", "confirm"),
  "knockout-restrict-buyer-excluded-industry": buyerKnockoutText("buyer-excluded-industry", "restrict"),
  // The confirm key carries this knockout's follow-up, which is the
  // good-standing question rather than a "to confirm" sentence.
  "knockout-statement-buyer-phoenix-default": buyerKnockoutText("buyer-phoenix-default", "statement"),
  "knockout-confirm-buyer-phoenix-default": buyerKnockoutText("buyer-phoenix-default", "confirm"),
  "knockout-restrict-buyer-phoenix-default": buyerKnockoutText("buyer-phoenix-default", "restrict"),
  "knockout-unsure-buyer-phoenix-default": buyerKnockoutText("buyer-phoenix-default", "unsure"),

  // ── Review: goals and who the documents are for (buyer only — the seller
  //    keeps its coded labels) ──────────────────────────────────────────
  "helper-buyer-goals": GOALS_HELPER,
  "label-buyer-first-name": "First name",
  "label-buyer-last-name": "Last name",
  "label-buyer-email": "Email",
  "helper-buyer-email": "Where your results and documents go.",
  "label-buyer-phone": "Phone",
  "helper-buyer-phone": "Optional — if you'd like us to call.",
  "helper-buyer-phone-given": "From the details you just gave us.",
} as const satisfies Record<string, string>;

/** A funnel question block key. */
export type FunnelQuestionKey = keyof typeof FUNNEL_QUESTION_DEFAULTS;

/** Every funnel question block, by key — always complete. */
export type FunnelQuestionCopy = Readonly<Record<FunnelQuestionKey, string>>;

/**
 * The published question map laid over the compiled defaults, keeping only the
 * keys this build knows.
 *
 * A key the payload carries is used AS SENT, blank included: a block published
 * empty means "show nothing here" (D4), exactly as the marketing fields behave,
 * so callers drop the element with `hasText` / `textOrUndefined` (`./blankCopy`).
 * Only a key the payload does not carry at all — an older server, a partial map,
 * no payload — renders its compiled default.
 */
export function withFunnelQuestionDefaults(
  published: Readonly<Record<string, string>> | null | undefined,
): FunnelQuestionCopy {
  const merged: Record<string, string> = { ...FUNNEL_QUESTION_DEFAULTS };
  for (const key of Object.keys(FUNNEL_QUESTION_DEFAULTS)) {
    const text = published?.[key];
    if (typeof text === "string") merged[key] = text;
  }
  return merged as FunnelQuestionCopy;
}

/**
 * The compiled-in default for every funnel fine-print block, in the wire shape.
 *
 * What `useFunnelCopy()` renders before — and instead of — the server's published
 * copy. Assembled here rather than inside the hook so the six constants have one
 * downstream consumer and the parity test has one object to compare.
 *
 * Typed as the wire DTO deliberately: a block added server-side without a field
 * here becomes a typecheck failure rather than a silently missing sentence.
 *
 * Declared BELOW all six constants, not beside the consent block above: these are
 * `const`, so they are not hoisted, and referencing `ADMINISTRATOR_LINE` from
 * earlier in the module would throw at import time.
 */
export const FUNNEL_COPY_FALLBACK: PrequalFunnelCopy = {
  consentLineBuyer: CONSENT_LINE_BUYER,
  consentLineSeller: CONSENT_LINE_SELLER,
  finePrintBuyer: FINE_PRINT_BUYER,
  finePrintSeller: FINE_PRINT_SELLER,
  indicativeLabel: INDICATIVE_LABEL,
  administratorLine: ADMINISTRATOR_LINE,
  marketing: FUNNEL_MARKETING_FALLBACK,
  callCertificationBuyer: CALL_CERTIFICATION_BUYER,
  questions: FUNNEL_QUESTION_DEFAULTS,
};

/** White-label: the locked, non-removable "Powered by Phoenix" attribution
 *  rendered on EVERY funnel view (native AND partner-themed). Phoenix-owned
 *  copy — never partner-themed, never overridable by a partner skin. */
export const POWERED_BY_PHOENIX =
  "Powered by Phoenix Capital Solutions, LLC · Loan & Note Administrator";

export const UNSURE_NOTE =
  "We’ll go over anything you marked “Unsure” with you — it’s fine to leave those as they are for now.";

// ── Saved progress (decision 22) ────────────────────────────────────────
//
// The disclosure is shown BEFORE the first save, not after it. We are keeping
// a stranger's partial financial answers on file, and the honest moment to say
// so is the moment it starts — with, in the same breath, how to get back to
// them. Every line below is written to be true on its own path: the organic
// visitor is told the link is shown once and never emailed, because that is
// exactly the design, and the invited visitor is pointed at the link they
// already have rather than promised a second one.

export const SAVE_PROGRESS_TITLE = "Save your progress";

export const SAVE_PROGRESS_DISCLOSURE =
  "Add your name and email and we’ll keep your answers on file as you go, so you can stop here and come back to them later instead of starting over. Everything you’ve entered stays saved from that moment on. Nothing is submitted until you finalize.";

export const SAVE_PROGRESS_LINK_WARNING =
  "When you save, we’ll show you a private link back to this application. That link is the only way back in — we don’t email it to you, and it’s shown once.";

export const SAVE_PROGRESS_SAVE_ACTION = "Save my progress";

export const SAVE_PROGRESS_SAVED_TITLE = "Your progress is saved";

export const SAVE_PROGRESS_SAVED_BODY =
  "Keep this link somewhere safe. It’s the only way back into this application — we don’t email it to you, and it won’t be shown again.";

/** Saved, but this view did not mint the link (a resumed session). Never
 *  implies a link is on its way: none is. */
export const SAVE_PROGRESS_SAVED_NO_LINK =
  "Your answers are saved as you go. Come back any time using the link you were given — we don’t email it to you.";

export const SAVE_PROGRESS_INVITED =
  "Your answers are saved as you go, so you can stop here and come back to them later. Use the same link that was emailed to you to pick up where you left off.";

export const SAVE_PROGRESS_RELOAD_ACTION = "Reload this application";
